import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Divider, Typography } from '@mui/material';
import { ordersPathTo, providersPathTo } from '../utils/Api';
import { currency } from './building_blocks/Currency';
import ProductPhotoPlaceholder from '../images/placeholder_product_image.png';
import { apiBaseUrl } from '../utils/Api/Api';
import visa from '../images/visa_card.png';
import master from '../images/master_card.png';
import SurfaceCard from './building_blocks/SurfaceCard';
import ProviderCard from './building_blocks/ProviderCard';
import PageHeader from './building_blocks/PageHeader';
import OrderReview from './OrderReview';

/* Icons */
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { layoutCardRadius } from '../theme/colors';

const FieldInfo = ({ label, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch' }}>
    <Typography variant="sectionTitle">{label}</Typography>
    {children}
  </Box>
);

const Price = ({ label, children, isTotal = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', alignSelf: 'stretch' }}>
    <Typography
      sx={{
        fontFamily: 'Nunito',
        fontSize: '14px',
        lineHeight: '24px',
        fontWeight: 400,
        color: isTotal ? 'text.primary' : 'text.secondary',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: 'Nunito',
        fontSize: isTotal ? '18px' : '14px',
        lineHeight: '24px',
        fontWeight: isTotal ? 700 : 600,
        color: isTotal ? 'primary.main' : 'text.primary',
      }}
    >
      {children}
    </Typography>
  </Box>
);

const statusLabels = {
  open: 'Pedido pendente',
  pending: 'Pedido pendente',
  paid: 'Pedido confirmado',
  accepted: 'Pedido confirmado',
  courier: 'Em entrega',
  delivered: 'Entregue',
  shipped: 'Entregue',
  done: 'Finalizado',
  failed: 'Pagamento falhou',
  rejected: 'Cancelado',
  canceled: 'Cancelado',
};

const formatOrderDate = (date) => {
  const dateMatch = date?.match(/^(\d{2})\/(\d{2})\/\d{2} às (\d{2}):(\d{2})/);
  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

  if (dateMatch) {
    const [, day, month, hour, minute] = dateMatch;
    return `${day}/${monthNames[Number(month) - 1]} às ${hour}h${minute}`;
  }

  return date;
};

const OrderDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [order, setOrder] = useState({});
  const [provider, setProvider] = useState(null);

  useEffect(() => {
    ordersPathTo.get(id).then((result) => {
      setOrder(result.data);
      document.title = `Pedido #${result.data.id} - TudoPets.app`;

      const nestedProvider = result.data.provider;
      setProvider(nestedProvider ? {
        ...nestedProvider,
        business_logo: result.data.provider_business_logo,
        business_banner: undefined,
      } : null);

      if (nestedProvider?.id) {
        providersPathTo.get({ id: nestedProvider.id })
          .then((providerResult) => setProvider(providerResult.data))
          .catch(() => {});
      }
    });
  }, [id]);

  if (!order.provider) return null;

  const paymentMethod = order.payment_method;
  const subtotal = Number(order.total_amount) || 0;
  const deliveryDiscount = Number(order.campaign_delivery_discount_amount) || 0;
  const platformDiscount = Number(order.campaign_platform_fee_discount_amount) || 0;
  const deliveryFeeCharged = Number(order.delivery_fee_charged ?? 1000);
  const deliveryFee = deliveryFeeCharged + deliveryDiscount;
  const totalDue = Number(order.charge_amount) || (subtotal + deliveryFeeCharged - platformDiscount);
  const statusLabel = statusLabels[order.status] || order.status;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
        <PageHeader title="Seu pedido" onBack={() => window.history.back()} />

        <Box
          sx={{
            display: 'flex',
            height: '26px',
            px: 1.5,
            alignItems: 'center',
            gap: 0.75,
            borderRadius: layoutCardRadius,
            bgcolor: 'surface.muted',
          }}
        >
          <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', fontWeight: 400, lineHeight: '21px', color: 'text.secondary' }}>
            {statusLabel}
            {order.date ? ` • ${formatOrderDate(order.date)}` : ''}
          </Typography>
        </Box>

        {order.status === 'delivered' && <OrderReview setOrder={setOrder} order={order} />}

        {provider && (
          <ProviderCard
            provider={provider}
            showProducts={false}
            showBanner={true}
            categories
            onCardClick={() => navigate(`/provider/${provider.id}`)}
          />
        )}

        <FieldInfo label="Entregar em:">
          <SurfaceCard sx={{ flexDirection: 'column', gap: 1 }}>
            {order.delivery_address ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <LocationOnIcon sx={{ color: 'primary.main' }} />
                <Typography sx={{ fontFamily: 'Nunito', fontSize: '16px', fontWeight: 600, lineHeight: '20px', color: 'text.primary', overflowWrap: 'anywhere' }}>
                  {order.delivery_address}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', color: 'text.secondary' }}>
                Endereço não informado
              </Typography>
            )}
          </SurfaceCard>
        </FieldInfo>

        <FieldInfo label="Produtos">
          <SurfaceCard sx={{ flexDirection: 'column', gap: 0, p: 0 }}>
            {order.order_items?.map((orderItem, index) => (
              <React.Fragment key={orderItem.id}>
                {index > 0 && <Divider sx={{ alignSelf: 'stretch' }} />}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1.5, sm: 2 },
                    py: 1.5,
                    px: { xs: 1.5, sm: 3 },
                    alignSelf: 'stretch',
                    width: '100%',
                  }}
                >
                  <Box
                    component="img"
                    alt={orderItem.title}
                    src={orderItem.item_photo ? `${apiBaseUrl}${orderItem.item_photo}` : ProductPhotoPlaceholder}
                    sx={{
                      width: { xs: 64, sm: 80 },
                      height: { xs: 64, sm: 80 },
                      borderRadius: '16px',
                      objectFit: 'cover',
                      bgcolor: 'surface.input',
                      flexShrink: 0,
                    }}
                  />

                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, flex: 1, minWidth: 0 }}>
                    <Typography variant="storeName">{orderItem.title}</Typography>
                    <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', fontWeight: 700, lineHeight: '21px', color: 'text.secondary' }}>
                      {currency(orderItem.price * orderItem.quantity)} ({orderItem.quantity} {Number(orderItem.quantity) === 1 ? 'unidade' : 'unidades'})
                    </Typography>
                  </Box>
                </Box>
              </React.Fragment>
            ))}
          </SurfaceCard>
        </FieldInfo>

        <FieldInfo label="Valores">
          <SurfaceCard sx={{ flexDirection: 'column' }}>
            <Price label="Subtotal:">{currency(subtotal)}</Price>
            <Price label="Taxa de entrega:">{currency(deliveryFee)}</Price>
            {deliveryDiscount > 0 && (
              <Price label="Desconto campanha na entrega:">-{currency(deliveryDiscount)}</Price>
            )}
            {platformDiscount > 0 && (
              <Price label="Desconto campanha TudoPets.app:">-{currency(platformDiscount)}</Price>
            )}
            <Divider sx={{ width: '100%' }} />
            <Price label="Total:" isTotal>{currency(totalDue)}</Price>
          </SurfaceCard>
        </FieldInfo>

        <FieldInfo label="Forma de pagamento">
          <SurfaceCard sx={{ flexDirection: 'column' }}>
            {paymentMethod?.type === 'pix' && (
              <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', lineHeight: '24px', fontWeight: 700 }}>
                Pix
              </Typography>
            )}

            {paymentMethod?.type === 'card' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, alignSelf: 'stretch' }}>
                <Box sx={{ flexShrink: 0, lineHeight: 0 }}>
                  {paymentMethod.brand === 'visa' ? (
                    <img alt="Visa" src={visa} style={{ width: '56px', height: '36px', display: 'block' }} />
                  ) : (
                    <img alt="Mastercard" src={master} style={{ width: '56px', height: '36px', display: 'block' }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <Typography sx={{ fontFamily: 'Nunito', fontSize: '16px', fontWeight: 600, lineHeight: '20px', textTransform: 'capitalize' }}>
                    {paymentMethod.brand} • {paymentMethod.funding === 'debit' ? 'Débito' : 'Crédito'}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', lineHeight: '21px', color: 'text.secondary' }}>
                    **** {paymentMethod.last4}
                  </Typography>
                </Box>
              </Box>
            )}
          </SurfaceCard>
        </FieldInfo>
      </Box>
    </Box>
  );
};

export default OrderDetails;
